import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "md:flex gap-4" }
const _hoisted_2 = { class: "lg:w-1/2 space-y-4" }
const _hoisted_3 = { class: "space-y-2" }
const _hoisted_4 = { class: "lg:w-1/2 mt-4 md:mt-0 space-y-3" }
const _hoisted_5 = { class: "grid grid-cols-4 gap-2" }
const _hoisted_6 = {
  key: 2,
  class: "flex items-center justify-center py-6 text-[1.2rem] text-gray-500"
}
const _hoisted_7 = { class: "text-[2rem] font-semibold text-gray-800" }
const _hoisted_8 = { class: "mt-[1.2rem] text-[1.4rem] text-gray-500" }

import { onMounted, ref, watch, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useSeo } from "@/composables/useSeo";
import SearchResultSection from "@/components/search/searchResultSection.vue";
import SpecialistsAvailableSection from "@/components/search/specialistsAvailableSection.vue";
import LayoutSection from "@/components/design/layoutSection.vue";
import InlineSpinner from "@/components/loader/InlineSpinner.vue";
import { useSearch } from "@/composables";
import type { CalendarRequestPayload } from "@/types/domain";


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchView',
  setup(__props) {

const { t } = useI18n();
useSeo({ title: t("seoSearchTitle"), description: t("seoSearchDesc") });

const loadMoreSentinel = ref<HTMLElement | null>(null);

// Use the refactored composable
const {
  doctors,
  filters,
  availabilityLoading,
  currentWindowStart,
  hasSearched,
  datesLoading,
  hasMoreResults,
  isLoadingMore,
  handleRequestMoreDates,
  handleRequestCalendar,
  initializeLocation,
  search,
  loadMore,
  handleSearchArea,
} = useSearch({
  translateFn: t,
});

// Event handlers
async function handleSearch() {
  await search();
}

function onRequestMoreDates(doctor: any, payload: CalendarRequestPayload) {
  handleRequestMoreDates(doctor, payload);
}

function onRequestCalendar(doctor: any, payload: CalendarRequestPayload) {
  handleRequestCalendar(doctor, payload);
}

function onSearchArea(payload: {
  latitude: number | null;
  longitude: number | null;
  radiusKm?: number | null;
}) {
  handleSearchArea(payload);
}

// Intersection Observer for infinite scroll
let loadMoreObserver: IntersectionObserver | null = null;

function setupObserver() {
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
    loadMoreObserver = null;
  }

  if (!hasMoreResults.value || !loadMoreSentinel.value) return;
  if (typeof IntersectionObserver === "undefined") return;

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && !isLoadingMore.value) {
        loadMore();
      }
    },
    { rootMargin: "200px" },
  );

  loadMoreObserver.observe(loadMoreSentinel.value);
}

// Watch for changes that require observer update
watch([hasMoreResults, () => doctors.value.length], () => {
  setupObserver();
});

// Lifecycle hooks
onMounted(async () => {
  await initializeLocation();
  await search();
});

onBeforeUnmount(() => {
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", null, [
    _createVNode(SearchResultSection, {
      "result-count": _unref(doctors).length,
      loading: _unref(availabilityLoading),
      onSearch: handleSearch
    }, null, 8, ["result-count", "loading"]),
    (_unref(availabilityLoading) && !_unref(doctors).length)
      ? (_openBlock(), _createBlock(LayoutSection, {
          key: 0,
          class: "space-y-8"
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (n) => {
              return _createElementVNode("div", {
                key: n,
                class: "bg-white-50 rounded-[2rem] p-4 sm:p-6 border border-[rgba(28,27,26,0.06)]"
              }, [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", _hoisted_2, [
                    _cache[0] || (_cache[0] = _createElementVNode("div", { class: "flex items-center gap-3" }, [
                      _createElementVNode("div", { class: "w-[6.4rem] h-[6.4rem] rounded-full bg-black/[0.06] animate-pulse" }),
                      _createElementVNode("div", { class: "space-y-2" }, [
                        _createElementVNode("div", { class: "h-4 w-40 rounded bg-black/[0.06] animate-pulse" }),
                        _createElementVNode("div", { class: "h-3 w-24 rounded bg-black/[0.05] animate-pulse" })
                      ])
                    ], -1)),
                    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "h-[3.4rem] w-1/2 rounded-full bg-black/[0.05] animate-pulse" }, null, -1)),
                    _createElementVNode("div", _hoisted_3, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (r) => {
                        return _createElementVNode("div", {
                          key: r,
                          class: "h-[4.4rem] rounded-[1.2rem] bg-black/[0.05] animate-pulse"
                        })
                      }), 64))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "h-5 w-40 mx-auto rounded bg-black/[0.06] animate-pulse" }, null, -1)),
                    _createElementVNode("div", _hoisted_5, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(16, (c) => {
                        return _createElementVNode("div", {
                          key: c,
                          class: "h-[3.6rem] rounded-lg bg-black/[0.05] animate-pulse"
                        })
                      }), 64))
                    ])
                  ])
                ])
              ])
            }), 64))
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(SpecialistsAvailableSection, {
      doctors: _unref(doctors),
      "dates-loading": _unref(datesLoading),
      "base-date": _unref(currentWindowStart),
      "search-loading": _unref(availabilityLoading),
      "show-minimap": !_unref(filters).online,
      onRequestMoreDates: onRequestMoreDates,
      onRequestCalendar: onRequestCalendar,
      onSearchArea: onSearchArea
    }, null, 8, ["doctors", "dates-loading", "base-date", "search-loading", "show-minimap"]),
    (_unref(hasMoreResults) && _unref(doctors).length)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "loadMoreSentinel",
          ref: loadMoreSentinel,
          class: "h-6"
        }, null, 512))
      : _createCommentVNode("", true),
    (_unref(isLoadingMore))
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(InlineSpinner, { size: "24" })
        ]))
      : _createCommentVNode("", true),
    (
        _unref(hasSearched) && !_unref(availabilityLoading) && !_unref(doctors).length && !_unref(isLoadingMore)
      )
      ? (_openBlock(), _createBlock(LayoutSection, {
          key: 3,
          maxWidthClass: 'w-full',
          sectionClass: "flex flex-col items-center justify-center min-h-[40vh] text-center"
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t("No results found")), 1),
            _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t("Try adjusting your filters to discover other specialists.")), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})